import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  createAircraftDocumentUrl,
  deleteAircraftDocument,
  fetchAircraftDocuments,
  uploadAircraftDocument,
} from './services/aircraftDocumentsService';

const documentTypes = [
  'POH',
  'Weight & Balance',
  'ARC',
  'Insurance',
  'Radio Licence',
  'Noise Certificate',
  'Maintenance Schedule',
  'Other',
];

export default function AircraftDocumentsPanel({
  aircraft,
}) {
  const aircraftId = aircraft?.id;
  const organisationId = aircraft?.organisation_id;

  const [documents, setDocuments] = useState([]);
  const [documentType, setDocumentType] =
    useState('POH');
  const [expiryDate, setExpiryDate] = useState('');
  const [selectedFile, setSelectedFile] =
    useState(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] =
    useState(false);

  const [openingDocumentId, setOpeningDocumentId] =
    useState('');

  const [deletingDocumentId, setDeletingDocumentId] =
    useState('');

  const [statusMessage, setStatusMessage] =
    useState('');

  const [errorMessage, setErrorMessage] =
    useState('');

  const loadDocuments = useCallback(async () => {
    if (!aircraftId) {
      setDocuments([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const rows =
        await fetchAircraftDocuments(aircraftId);

      setDocuments(rows);
    } catch (error) {
      console.error(
        'Aircraft documents load failed:',
        error
      );

      setDocuments([]);

      setErrorMessage(
        error?.message ||
          'Could not load aircraft documents.'
      );
    } finally {
      setLoading(false);
    }
  }, [aircraftId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  function handleFileSelected(event) {
    const file =
      event.target.files?.[0] || null;

    setSelectedFile(file);
    setStatusMessage('');
    setErrorMessage('');
  }

  async function handleUploadDocument() {
    setStatusMessage('');
    setErrorMessage('');

    if (!selectedFile) {
      setErrorMessage(
        'Please select a document to upload.'
      );
      return;
    }

    if (!aircraftId) {
      setErrorMessage('Aircraft ID is missing.');
      return;
    }

    if (!organisationId) {
      setErrorMessage(
        'Organisation ID is missing for this aircraft.'
      );
      return;
    }

    const maxBytes = 10 * 1024 * 1024;

    if (selectedFile.size > maxBytes) {
      setErrorMessage(
        'Document too large. Please upload a file under 10 MB.'
      );
      return;
    }

    setUploading(true);

    try {
      await uploadAircraftDocument({
        aircraftId,
        organisationId,
        documentType,
        expiryDate,
        file: selectedFile,
        userId: null,
      });

      setSelectedFile(null);
      setExpiryDate('');
      setDocumentType('POH');

      setStatusMessage(
        'Aircraft document uploaded successfully.'
      );

      const fileInput =
        document.getElementById(
          `aircraft-document-file-${aircraftId}`
        );

      if (fileInput) {
        fileInput.value = '';
      }

      await loadDocuments();
    } catch (error) {
      console.error(
        'Aircraft document upload failed:',
        error
      );

      setErrorMessage(
        error?.message ||
          'Could not upload this document.'
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleOpenDocument(documentRow) {
    if (!documentRow?.file_path) return;

    setOpeningDocumentId(documentRow.id);
    setStatusMessage('');
    setErrorMessage('');

    try {
      const signedUrl =
        await createAircraftDocumentUrl(
          documentRow.file_path
        );

      const newWindow = window.open(
        signedUrl,
        '_blank',
        'noopener,noreferrer'
      );

      if (!newWindow) {
        setErrorMessage(
          'Could not open the document. Please allow pop-ups and try again.'
        );
      }
    } catch (error) {
      console.error(
        'Aircraft document open failed:',
        error
      );

      setErrorMessage(
        error?.message ||
          'Could not open this document.'
      );
    } finally {
      setOpeningDocumentId('');
    }
  }

  async function handleDeleteDocument(documentRow) {
    if (!documentRow?.id) return;

    const confirmed = window.confirm(
      `Delete ${
        documentRow.file_name ||
        'this document'
      }?`
    );

    if (!confirmed) return;

    setDeletingDocumentId(documentRow.id);
    setStatusMessage('');
    setErrorMessage('');

    try {
      await deleteAircraftDocument(documentRow);

      setStatusMessage(
        'Aircraft document deleted successfully.'
      );

      await loadDocuments();
    } catch (error) {
      console.error(
        'Aircraft document deletion failed:',
        error
      );

      setErrorMessage(
        error?.message ||
          'Could not delete this document.'
      );
    } finally {
      setDeletingDocumentId('');
    }
  }

  return (
    <div style={styles.panel}>
      <div style={styles.headingRow}>
        <div style={styles.heading}>
          Aircraft Documents
        </div>

        <button
          type="button"
          onClick={loadDocuments}
          disabled={loading || uploading}
          style={{
            ...styles.refreshButton,
            opacity:
              loading || uploading ? 0.65 : 1,
            cursor:
              loading || uploading
                ? 'wait'
                : 'pointer',
          }}
        >
          {loading ? 'LOADING...' : 'REFRESH'}
        </button>
      </div>

      <div style={styles.formGrid}>
        <label style={styles.label}>
          DOCUMENT TYPE
          <select
            value={documentType}
            onChange={(event) =>
              setDocumentType(event.target.value)
            }
            disabled={uploading}
            style={styles.input}
          >
            {documentTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>

        <label style={styles.label}>
          EXPIRY DATE
          <input
            type="date"
            value={expiryDate}
            onChange={(event) =>
              setExpiryDate(event.target.value)
            }
            disabled={uploading}
            style={styles.input}
          />
        </label>
      </div>

      <div style={styles.uploadRow}>
        <input
          id={`aircraft-document-file-${aircraftId}`}
          type="file"
          onChange={handleFileSelected}
          disabled={uploading}
          style={styles.hiddenFileInput}
        />

        <label
          htmlFor={`aircraft-document-file-${aircraftId}`}
          style={{
            ...styles.chooseFileButton,
            opacity: uploading ? 0.65 : 1,
            cursor: uploading
              ? 'wait'
              : 'pointer',
          }}
        >
          CHOOSE DOCUMENT
        </label>

        <button
          type="button"
          onClick={handleUploadDocument}
          disabled={uploading || !selectedFile}
          style={{
            ...styles.uploadButton,
            opacity:
              uploading || !selectedFile
                ? 0.65
                : 1,
            cursor:
              uploading || !selectedFile
                ? 'not-allowed'
                : 'pointer',
          }}
        >
          {uploading
            ? 'UPLOADING...'
            : 'UPLOAD DOCUMENT'}
        </button>
      </div>

      <div style={styles.helpText}>
        Maximum file size: 10 MB. Expiry date is
        optional.
      </div>

      {selectedFile && (
        <div style={styles.selectedFile}>
          <div style={styles.selectedFileLabel}>
            Selected document
          </div>

          <div style={styles.selectedFileName}>
            📄 {selectedFile.name}
          </div>
        </div>
      )}

      {statusMessage && (
        <div style={styles.statusMessage}>
          {statusMessage}
        </div>
      )}

      {errorMessage && (
        <div style={styles.errorMessage}>
          {errorMessage}
        </div>
      )}

      {loading && documents.length === 0 ? (
        <div style={styles.emptyMessage}>
          Loading aircraft documents...
        </div>
      ) : documents.length === 0 ? (
        <div style={styles.emptyMessage}>
          No documents uploaded for this aircraft.
        </div>
      ) : (
        <div style={styles.documentList}>
          {documents.map((documentRow) => {
            const expiryMeta = getExpiryMeta(
              documentRow.expiry_date
            );

            const isOpening =
              openingDocumentId ===
              documentRow.id;

            const isDeleting =
              deletingDocumentId ===
              documentRow.id;

            return (
              <div
                key={documentRow.id}
                style={styles.documentCard}
              >
                <div style={styles.documentHeader}>
                  <div style={styles.documentDetails}>
                    <div style={styles.documentType}>
                      {getDocumentTypeLabel(
                        documentRow.document_type
                      )}
                    </div>

                    <div style={styles.documentName}>
                      {documentRow.file_name ||
                        'Unnamed document'}
                    </div>
                  </div>

                  <div style={styles.actions}>
                    <button
                      type="button"
                      onClick={() =>
                        handleOpenDocument(
                          documentRow
                        )
                      }
                      disabled={
                        isOpening || isDeleting
                      }
                      style={{
                        ...styles.openButton,
                        opacity:
                          isOpening || isDeleting
                            ? 0.65
                            : 1,
                        cursor:
                          isOpening || isDeleting
                            ? 'wait'
                            : 'pointer',
                      }}
                    >
                      {isOpening
                        ? 'OPENING...'
                        : 'OPEN'}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteDocument(
                          documentRow
                        )
                      }
                      disabled={
                        isDeleting || isOpening
                      }
                      style={{
                        ...styles.deleteButton,
                        opacity:
                          isDeleting || isOpening
                            ? 0.65
                            : 1,
                        cursor:
                          isDeleting || isOpening
                            ? 'wait'
                            : 'pointer',
                      }}
                    >
                      {isDeleting
                        ? 'DELETING...'
                        : 'DELETE'}
                    </button>
                  </div>
                </div>

                <div style={styles.dateGrid}>
                  <div style={styles.dateBlock}>
                    <div style={styles.dateLabel}>
                      Uploaded
                    </div>

                    <div style={styles.dateValue}>
                      {formatDate(
                        documentRow.created_at
                      )}
                    </div>
                  </div>

                  <div style={styles.dateBlock}>
                    <div style={styles.dateLabel}>
                      Expiry
                    </div>

                    <div
                      style={{
                        ...styles.dateValue,
                        color: expiryMeta.colour,
                      }}
                    >
                      {expiryMeta.shortLabel}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.expiryMessage,
                    color: expiryMeta.colour,
                  }}
                >
                  {expiryMeta.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getDocumentTypeLabel(documentType) {
  const type = String(
    documentType || ''
  ).trim();

  const labels = {
    POH: '📘 POH',
    'Weight & Balance':
      '⚖️ Weight & Balance',
    ARC: '📋 ARC',
    Insurance: '🛡️ Insurance',
    'Radio Licence': '📻 Radio Licence',
    'Noise Certificate':
      '🔊 Noise Certificate',
    'Maintenance Schedule':
      '🔧 Maintenance Schedule',
    Other: '📄 Other',
  };

  return (
    labels[type] ||
    `📄 ${type || 'Document'}`
  );
}

function formatDate(value) {
  if (!value) return 'Unknown';

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getExpiryMeta(expiryDate) {
  if (!expiryDate) {
    return {
      label: 'No expiry date',
      shortLabel: 'Not required',
      colour: '#8db7e6',
    };
  }

  const expiry = new Date(
    `${expiryDate}T23:59:59`
  );

  if (!Number.isFinite(expiry.getTime())) {
    return {
      label: 'Expiry date unavailable',
      shortLabel: 'Unknown',
      colour: '#8db7e6',
    };
  }

  const today = new Date();

  const millisecondsPerDay =
    24 * 60 * 60 * 1000;

  const daysRemaining = Math.ceil(
    (expiry.getTime() - today.getTime()) /
      millisecondsPerDay
  );

  if (daysRemaining < 0) {
    return {
      label: `Expired ${Math.abs(
        daysRemaining
      )} days ago`,
      shortLabel: formatDate(expiryDate),
      colour: '#ff8d8d',
    };
  }

  if (daysRemaining <= 30) {
    return {
      label: `${daysRemaining} days remaining`,
      shortLabel: formatDate(expiryDate),
      colour: '#ff8d8d',
    };
  }

  if (daysRemaining <= 90) {
    return {
      label: `${daysRemaining} days remaining`,
      shortLabel: formatDate(expiryDate),
      colour: '#ffd27a',
    };
  }

  return {
    label: `${daysRemaining} days remaining`,
    shortLabel: formatDate(expiryDate),
    colour: '#8ff4b6',
  };
}

const styles = {
  panel: {
    marginTop: 8,
    padding: '0.75rem',
    border: '1px solid #35507f',
    borderRadius: 8,
    background: '#12182a',
  },

  headingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },

  heading: {
    color: '#b6eaff',
    fontSize: '1rem',
    fontWeight: 800,
    textTransform: 'uppercase',
  },

  refreshButton: {
    background: '#24446f',
    color: '#d7ebff',
    border: '1px solid #3a5f92',
    borderRadius: 8,
    padding: '0.35rem 0.65rem',
    fontWeight: 700,
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 10,
    marginBottom: 10,
  },

  label: {
    color: '#b6eaff',
    fontSize: '0.82rem',
    fontWeight: 700,
  },

  input: {
    display: 'block',
    width: '100%',
    marginTop: 4,
    padding: '0.45rem',
    borderRadius: 6,
    border: '1px solid #35507f',
    background: '#1a2338',
    color: '#d7ebff',
    boxSizing: 'border-box',
  },

  uploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },

  hiddenFileInput: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },

  chooseFileButton: {
    display: 'inline-block',
    background: '#24446f',
    color: '#ffffff',
    border: '1px solid #3a5f92',
    borderRadius: 8,
    padding: '0.5rem 0.8rem',
    fontWeight: 700,
  },

  uploadButton: {
    background: '#1ecfff',
    color: '#090a12',
    border: 'none',
    borderRadius: 8,
    padding: '0.5rem 0.8rem',
    fontWeight: 700,
  },

  helpText: {
    color: '#8db7e6',
    fontSize: '0.82rem',
    marginTop: 8,
  },

  selectedFile: {
    marginTop: 10,
    padding: '0.65rem 0.75rem',
    border: '1px solid #35507f',
    borderRadius: 8,
    background: '#1a2338',
    overflowWrap: 'anywhere',
  },

  selectedFileLabel: {
    color: '#8db7e6',
    fontSize: '0.78rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    marginBottom: 4,
  },

  selectedFileName: {
    color: '#ffffff',
    fontSize: '0.92rem',
    fontWeight: 700,
  },

  statusMessage: {
    color: '#1ecfff',
    marginTop: 10,
  },

  errorMessage: {
    color: '#ff8d8d',
    marginTop: 10,
  },

  emptyMessage: {
    color: '#8db7e6',
    marginTop: 12,
  },

  documentList: {
    display: 'grid',
    gap: 10,
    marginTop: 12,
  },

  documentCard: {
    padding: '0.85rem',
    borderRadius: 10,
    border: '1px solid #2f466d',
    background: '#1a2338',
  },

  documentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
  },

  documentDetails: {
    minWidth: 0,
    flex: 1,
  },

  documentType: {
    color: '#1ecfff',
    fontSize: '0.82rem',
    fontWeight: 800,
    textTransform: 'uppercase',
  },

  documentName: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.95rem',
    marginTop: 3,
    overflowWrap: 'anywhere',
  },

  dateGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 8,
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid #2f466d',
  },

  dateBlock: {
    minWidth: 0,
  },

  dateLabel: {
    color: '#8db7e6',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    marginBottom: 2,
  },

  dateValue: {
    color: '#d7ebff',
    fontSize: '0.88rem',
    fontWeight: 700,
  },

  expiryMessage: {
    fontSize: '0.8rem',
    fontWeight: 700,
    marginTop: 6,
  },

  actions: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },

  openButton: {
    background: '#9ed0ff',
    color: '#10233a',
    border: 'none',
    borderRadius: 8,
    padding: '0.4rem 0.65rem',
    fontWeight: 700,
  },

  deleteButton: {
    background: '#ff7c7c',
    color: '#290707',
    border: 'none',
    borderRadius: 8,
    padding: '0.4rem 0.65rem',
    fontWeight: 700,
  },
};